import { api } from './client';
import { useAuthStore } from '../store/authStore';
import type { Video } from '../types/models';

export type { Video };

export interface GetVideosParams {
  limit?: number;
  offset?: number;
  /** Only videos that belong to no group (for the main grid). */
  standalone?: boolean;
}

export interface StreamToken {
  url: string;
  token: string;
  expires: string;
}

export async function getVideos(params?: GetVideosParams): Promise<Video[]> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.standalone) query.set('standalone', 'true');

  const queryString = query.toString();
  const res = await api.get<{ videos: Video[] }>(
    `/api/videos${queryString ? `?${queryString}` : ''}`
  );
  return res.videos ?? [];
}

export async function getVideo(id: number): Promise<Video> {
  return api.get<Video>(`/api/videos/${id}`);
}

export async function updateVideo(
  id: number,
  params: { title?: string; description?: string }
): Promise<Video> {
  return api.patch<Video>(`/api/videos/${id}`, params);
}

export async function deleteVideo(id: number): Promise<void> {
  return api.delete<void>(`/api/videos/${id}`);
}

export function videoPosterUrl(video: Pick<Video, 'id' | 'posterPath'>): string | null {
  return video.posterPath
    ? `/api/videos/${video.id}/poster?v=${encodeURIComponent(video.posterPath)}`
    : null;
}

export async function repickVideoPoster(id: number, atSecs: number): Promise<Video> {
  return api.post<Video>(`/api/videos/${id}/poster`, { at_secs: atSecs }, { maxRetries: 0 });
}

export async function restoreVideoPoster(id: number): Promise<Video> {
  return api.post<Video>(`/api/videos/${id}/poster/restore`, {});
}

export async function getVideoStreamToken(id: number): Promise<StreamToken> {
  return api.get<StreamToken>(`/api/videos/${id}/stream-token`);
}

export interface VideoGroup {
  id: number;
  title: string;
  description?: string;
  kind: string;
  posterPath?: string;
  videoCount: number;
  /** First member video with a poster; used as the group card's cover image. */
  coverVideoId?: number;
  addedAt: string;
  updatedAt: string;
}

export async function getVideoGroups(): Promise<VideoGroup[]> {
  const res = await api.get<{ groups: VideoGroup[] }>('/api/video-groups');
  return res.groups ?? [];
}

export async function getVideoGroup(
  id: number
): Promise<{ group: VideoGroup; videos: Video[] }> {
  return api.get<{ group: VideoGroup; videos: Video[] }>(`/api/video-groups/${id}`);
}

export async function createVideoGroup(params: {
  title: string;
  description?: string;
  kind?: string;
}): Promise<VideoGroup> {
  return api.post<VideoGroup>('/api/video-groups', params);
}

export async function updateVideoGroup(
  id: number,
  params: { title?: string; description?: string; kind?: string }
): Promise<void> {
  return api.patch<void>(`/api/video-groups/${id}`, params);
}

export async function deleteVideoGroup(id: number): Promise<void> {
  return api.delete<void>(`/api/video-groups/${id}`);
}

export async function addVideoToGroup(groupId: number, videoId: number): Promise<void> {
  return api.post<void>(`/api/video-groups/${groupId}/videos`, { video_id: videoId });
}

export async function removeVideoFromGroup(groupId: number, videoId: number): Promise<void> {
  return api.delete<void>(`/api/video-groups/${groupId}/videos/${videoId}`);
}

export async function reorderVideoGroup(groupId: number, videoIds: number[]): Promise<void> {
  return api.put<void>(`/api/video-groups/${groupId}/order`, { video_ids: videoIds });
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface UploadVideoParams {
  file: File;
  title?: string;
  description?: string;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

// A successful upload returns the created video. On a content-hash match the
// server returns the pre-existing video flagged as a duplicate instead of
// re-storing the bytes — the group-authoring flow attaches it silently.
export interface UploadVideoResult {
  video: Video;
  duplicate: boolean;
}

// Multipart upload via XMLHttpRequest (not fetch) so we get real upload-progress
// events — fetch has no upload progress API. Videos are up to 5GB, so a progress
// bar is essential. Auth header is attached manually like the book uploader.
export async function uploadVideo({
  file,
  title,
  description,
  onProgress,
  signal,
}: UploadVideoParams): Promise<UploadVideoResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (title) formData.append('title', title);
  if (description) formData.append('description', description);

  const { accessToken } = useAuthStore.getState();

  return new Promise<UploadVideoResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/videos');
    xhr.withCredentials = true;
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return;
      onProgress({
        loaded: e.loaded,
        total: e.total,
        percent: Math.round((e.loaded / e.total) * 100),
      });
    };

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(xhr.responseText || `Upload failed (${xhr.status})`));
        return;
      }
      // The upload handler responds with the bare video on create, or
      // {duplicate:true, video} when the content hash already exists.
      try {
        const body = JSON.parse(xhr.responseText) as
          | Video
          | { duplicate: true; video: Video };
        if ('duplicate' in body && body.duplicate) {
          resolve({ video: body.video, duplicate: true });
        } else {
          resolve({ video: body as Video, duplicate: false });
        }
      } catch {
        reject(new Error('Upload succeeded but the response could not be parsed'));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));

    xhr.send(formData);
  });
}

export interface VideoGroupCoverToken {
  url: string;
  token: string;
  expires: string;
}

export async function getVideoGroupCoverToken(id: number): Promise<VideoGroupCoverToken> {
  return api.get<VideoGroupCoverToken>(`/api/video-groups/${id}/cover-token`);
}

// Multipart upload via XMLHttpRequest, mirroring uploadAudioGroupCover — simple
// JSON-response upload, no progress bar needed since cover images are small
// (unlike the video uploads above).
export async function uploadVideoGroupCover(id: number, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('cover', file);

  const { accessToken } = useAuthStore.getState();

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/video-groups/${id}/cover`);
    xhr.withCredentials = true;
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(xhr.responseText || `Cover upload failed (${xhr.status})`));
        return;
      }
      resolve();
    };
    xhr.onerror = () => reject(new Error('Cover upload failed: network error'));

    xhr.send(formData);
  });
}
